import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';

interface PageEngagement {
  pagePath: string;
  pageTitle: string;
  views: number;
  avgEngagementTime: number; // in seconds
  bounceRate: number;
  users: number;
}

/**
 * Fetch GA4 page engagement data for the Engagement Matrix
 * Combines traffic volume with engagement quality metrics
 */
export function useGA4PageEngagement(limit = 100) {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;

  return useQuery({
    queryKey: ['ga4-page-engagement', ga4PropertyId, limit],
    queryFn: async (): Promise<PageEngagement[] | null> => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      try {
        const request = {
          propertyId: ga4PropertyId,
          dateRange: { startDate: '28daysAgo', endDate: 'yesterday' },
          dimensions: ['pagePath', 'pageTitle'],
          metrics: ['screenPageViews', 'averageSessionDuration', 'bounceRate', 'totalUsers'],
          orderBy: { metric: 'screenPageViews', desc: true },
          limit,
        };

        const response = await api.google.runGAReport(request);

        if (!response?.rows) {
          return [];
        }

        return response.rows.map((row: any) => ({
          pagePath: row.dimensionValues?.[0]?.value || '',
          pageTitle: row.dimensionValues?.[1]?.value || '',
          views: parseInt(row.metricValues?.[0]?.value || '0', 10),
          avgEngagementTime: parseFloat(row.metricValues?.[1]?.value || '0'),
          bounceRate: parseFloat(row.metricValues?.[2]?.value || '0') * 100,
          users: parseInt(row.metricValues?.[3]?.value || '0', 10),
        }));
      } catch (error) {
        console.error('Failed to fetch GA4 page engagement:', error);
        return [];
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}
